import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  RefreshCw, 
  Plus,
  X,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const PivotTableWidget = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Pivot configuration
  const [rowFields, setRowFields] = useState(["source"]);
  const [columnFields, setColumnFields] = useState(["status"]);
  const [valueField, setValueField] = useState("status");
  const [summarizeBy, setSummarizeBy] = useState("counta");
  const [showAs, setShowAs] = useState("default");
  const [rowOrder, setRowOrder] = useState("asc");
  const [columnOrder, setColumnOrder] = useState("asc");
  const [rowSortBy, setRowSortBy] = useState("source");
  const [columnSortBy, setColumnSortBy] = useState("status");
  const [showRowTotals, setShowRowTotals] = useState(true);
  const [showColumnTotals, setShowColumnTotals] = useState(true);
  const [filters, setFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  
  // UI state
  const [suggestedExpanded, setSuggestedExpanded] = useState(false);
  const [rowAddOpen, setRowAddOpen] = useState(false);
  const [columnAddOpen, setColumnAddOpen] = useState(false);
  const [filterAddOpen, setFilterAddOpen] = useState(false);
  
  const availableFields = [
    "stage", "status", "source", "current_location", 
    "preferred_shift", "experience", "assigned_telecaller", "vehicle"
  ];
  
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    fetchPivotData();
  }, []);

  const fetchPivotData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Build filters object
      const activeFilters = {};
      filters.forEach(f => {
        if (filterValues[f] && filterValues[f] !== "all") {
          activeFilters[f] = filterValues[f];
        }
      });
      
      const response = await axios.post(`${API}/driver-onboarding/pivot-table`, {
        rows: rowFields,
        columns: columnFields,
        values: "count",
        filters: activeFilters
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRawData(response.data.data || []);
      setFilterOptions(response.data.filter_options || {});
      
      // Get total count
      const summaryRes = await axios.get(`${API}/driver-onboarding/pivot-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotal(summaryRes.data.total || 0);
      
    } catch (error) {
      console.error("Failed to fetch pivot data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Process raw data into pivot table format
  const pivotData = useMemo(() => {
    if (!rawData.length || !rowFields.length) return { rows: [], columns: [], matrix: {}, rowTotals: {}, columnTotals: {}, grandTotal: 0 };
    
    const rowKey = rowFields[0];
    const colKey = columnFields[0];
    
    const rowValues = new Set();
    const columnValues = new Set();
    const matrix = {};
    const rowTotals = {};
    const columnTotals = {};
    let grandTotal = 0;
    
    rawData.forEach(item => {
      const row = item._id?.[rowKey] || '(Empty)';
      const col = colKey ? (item._id?.[colKey] || '(Empty)') : 'Count';
      const count = item.count || 0;
      
      rowValues.add(row);
      columnValues.add(col);
      
      if (!matrix[row]) matrix[row] = {};
      matrix[row][col] = (matrix[row][col] || 0) + count;
      
      rowTotals[row] = (rowTotals[row] || 0) + count;
      columnTotals[col] = (columnTotals[col] || 0) + count;
      grandTotal += count;
    });
    
    // Sort rows
    const sortedRows = Array.from(rowValues).sort((a, b) => {
      if (a === '(Empty)') return 1;
      if (b === '(Empty)') return -1;
      const comparison = a.toString().localeCompare(b.toString());
      return rowOrder === 'asc' ? comparison : -comparison;
    });
    
    // Sort columns
    const sortedColumns = Array.from(columnValues).sort((a, b) => {
      if (a === '(Empty)') return 1;
      if (b === '(Empty)') return -1;
      const comparison = a.toString().localeCompare(b.toString());
      return columnOrder === 'asc' ? comparison : -comparison;
    });
    
    return {
      rows: sortedRows,
      columns: sortedColumns,
      matrix,
      rowTotals,
      columnTotals,
      grandTotal
    };
  }, [rawData, rowFields, columnFields, rowOrder, columnOrder]);

  const formatFieldName = (field) => {
    return field?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const handleApply = () => {
    fetchPivotData();
  };

  const handleClearAll = () => {
    setRowFields([]);
    setColumnFields([]);
    setFilters([]);
    setFilterValues({});
    setShowRowTotals(true);
    setShowColumnTotals(true);
  };

  const addRowField = (field) => {
    if (!rowFields.includes(field)) {
      setRowFields([...rowFields, field]);
      setRowSortBy(field);
    }
    setRowAddOpen(false);
  };

  const removeRowField = (field) => {
    setRowFields(rowFields.filter(f => f !== field));
  };

  const addColumnField = (field) => {
    if (!columnFields.includes(field)) {
      setColumnFields([...columnFields, field]);
      setColumnSortBy(field);
    }
    setColumnAddOpen(false);
  };

  const removeColumnField = (field) => {
    setColumnFields(columnFields.filter(f => f !== field));
  };

  const addFilter = (field) => {
    if (!filters.includes(field)) {
      setFilters([...filters, field]);
    }
    setFilterAddOpen(false);
  };

  const removeFilter = (field) => {
    setFilters(filters.filter(f => f !== field));
    const newFilterValues = { ...filterValues };
    delete newFilterValues[field];
    setFilterValues(newFilterValues);
  };

  const getAvailableForRows = () => availableFields.filter(f => !rowFields.includes(f));
  const getAvailableForColumns = () => availableFields.filter(f => !columnFields.includes(f));
  const getAvailableForFilters = () => availableFields.filter(f => !filters.includes(f));

  return (
    <div className="flex bg-background rounded-xl border overflow-hidden" style={{ height: '700px' }} data-testid="pivot-table-widget">
      {/* Main Pivot Table Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {summarizeBy.toUpperCase()} of {columnFields[0] || 'records'}
            </span>
            <span className="text-xs text-muted-foreground">
              ({total.toLocaleString()} total records)
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleApply} disabled={loading}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        
        {/* Table Container */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : rowFields.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <p>Add a row field to start building your pivot table</p>
            </div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted/50">
                  <th className="border px-2 py-1.5 text-left font-semibold bg-green-100 dark:bg-green-900/30 min-w-[150px]">
                    {formatFieldName(rowFields[0])}
                  </th>
                  {pivotData.columns.map(col => (
                    <th key={col} className="border px-2 py-1.5 text-center font-medium bg-muted/30 min-w-[80px] max-w-[120px] truncate" title={col}>
                      {col}
                    </th>
                  ))}
                  {showRowTotals && (
                    <th className="border px-2 py-1.5 text-center font-semibold bg-green-50 dark:bg-green-900/20 min-w-[80px]">
                      Grand Total
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pivotData.rows.map((row, idx) => (
                  <tr key={row} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/10'}>
                    <td className="border px-2 py-1 font-medium truncate max-w-[200px]" title={row}>
                      {row}
                    </td>
                    {pivotData.columns.map(col => {
                      const value = pivotData.matrix[row]?.[col];
                      let displayValue = value || '';
                      
                      // Show as percentage if selected
                      if (showAs === 'percent_row' && value && pivotData.rowTotals[row]) {
                        displayValue = ((value / pivotData.rowTotals[row]) * 100).toFixed(1) + '%';
                      } else if (showAs === 'percent_col' && value && pivotData.columnTotals[col]) {
                        displayValue = ((value / pivotData.columnTotals[col]) * 100).toFixed(1) + '%';
                      } else if (showAs === 'percent_total' && value && pivotData.grandTotal) {
                        displayValue = ((value / pivotData.grandTotal) * 100).toFixed(1) + '%';
                      }
                      
                      return (
                        <td key={col} className="border px-2 py-1 text-center">
                          {displayValue}
                        </td>
                      );
                    })}
                    {showRowTotals && (
                      <td className="border px-2 py-1 text-center font-semibold bg-green-50/50 dark:bg-green-900/10">
                        {pivotData.rowTotals[row]?.toLocaleString() || 0}
                      </td>
                    )}
                  </tr>
                ))}
                {showColumnTotals && (
                  <tr className="bg-green-50 dark:bg-green-900/20 font-semibold">
                    <td className="border px-2 py-1.5">Grand Total</td>
                    {pivotData.columns.map(col => (
                      <td key={col} className="border px-2 py-1.5 text-center">
                        {pivotData.columnTotals[col]?.toLocaleString() || 0}
                      </td>
                    ))}
                    {showRowTotals && (
                      <td className="border px-2 py-1.5 text-center bg-green-100 dark:bg-green-900/30">
                        {pivotData.grandTotal.toLocaleString()}
                      </td>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Right Sidebar - Google Sheets Style */}
      <div className="w-72 border-l bg-[#f8f9fa] dark:bg-muted/20 flex flex-col">
        <div className="p-3 border-b flex items-center justify-between bg-white dark:bg-background">
          <span className="text-base font-medium">Pivot table editor</span>
          <button 
            onClick={handleClearAll}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Clear all
          </button>
        </div>
        
        <div className="flex-1 overflow-auto p-3 space-y-4">
          {/* Suggested Section */}
          <div>
            <button 
              onClick={() => setSuggestedExpanded(!suggestedExpanded)}
              className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground"
            >
              <span>Suggested</span>
              {suggestedExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {/* Rows Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Rows</span>
              <Popover open={rowAddOpen} onOpenChange={setRowAddOpen}>
                <PopoverTrigger asChild>
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                    Add
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="end">
                  <div className="space-y-1">
                    {getAvailableForRows().map(field => (
                      <button
                        key={field}
                        onClick={() => addRowField(field)}
                        className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                      >
                        {formatFieldName(field)}
                      </button>
                    ))}
                    {getAvailableForRows().length === 0 && (
                      <p className="text-xs text-muted-foreground px-2 py-1">All fields added</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {rowFields.map(field => (
              <div key={field} className="bg-white dark:bg-background rounded-lg p-3 space-y-2 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{formatFieldName(field)}</span>
                  <button onClick={() => removeRowField(field)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Order</Label>
                    <Select value={rowOrder} onValueChange={setRowOrder}>
                      <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Sort by</Label>
                    <Select value={rowSortBy} onValueChange={setRowSortBy}>
                      <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rowFields.map(f => (
                          <SelectItem key={f} value={f}>{formatFieldName(f)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox 
                    id={`show-row-totals-${field}`}
                    checked={showRowTotals}
                    onCheckedChange={setShowRowTotals}
                    className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  />
                  <label htmlFor={`show-row-totals-${field}`} className="text-xs cursor-pointer">
                    Show totals
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Columns Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Columns</span>
              <Popover open={columnAddOpen} onOpenChange={setColumnAddOpen}>
                <PopoverTrigger asChild>
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                    Add
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="end">
                  <div className="space-y-1">
                    {getAvailableForColumns().map(field => (
                      <button
                        key={field}
                        onClick={() => addColumnField(field)}
                        className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                      >
                        {formatFieldName(field)}
                      </button>
                    ))}
                    {getAvailableForColumns().length === 0 && (
                      <p className="text-xs text-muted-foreground px-2 py-1">All fields added</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {columnFields.map(field => (
              <div key={field} className="bg-white dark:bg-background rounded-lg p-3 space-y-2 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{formatFieldName(field)}</span>
                  <button onClick={() => removeColumnField(field)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Order</Label>
                    <Select value={columnOrder} onValueChange={setColumnOrder}>
                      <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Sort by</Label>
                    <Select value={columnSortBy} onValueChange={setColumnSortBy}>
                      <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {columnFields.map(f => (
                          <SelectItem key={f} value={f}>{formatFieldName(f)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox 
                    id={`show-col-totals-${field}`}
                    checked={showColumnTotals}
                    onCheckedChange={setShowColumnTotals}
                    className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  />
                  <label htmlFor={`show-col-totals-${field}`} className="text-xs cursor-pointer">
                    Show totals
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Values Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Values</span>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Add
              </button>
            </div>
            
            <div className="bg-white dark:bg-background rounded-lg p-3 space-y-2 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{formatFieldName(columnFields[0] || 'Count')}</span>
                <X className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Summarize by</Label>
                  <Select value={summarizeBy} onValueChange={setSummarizeBy}>
                    <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="counta">COUNTA</SelectItem>
                      <SelectItem value="count">COUNT</SelectItem>
                      <SelectItem value="sum">SUM</SelectItem>
                      <SelectItem value="average">AVERAGE</SelectItem>
                      <SelectItem value="min">MIN</SelectItem>
                      <SelectItem value="max">MAX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Show as</Label>
                  <Select value={showAs} onValueChange={setShowAs}>
                    <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="percent_row">% of row</SelectItem>
                      <SelectItem value="percent_col">% of column</SelectItem>
                      <SelectItem value="percent_total">% of grand total</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Filters</span>
              <Popover open={filterAddOpen} onOpenChange={setFilterAddOpen}>
                <PopoverTrigger asChild>
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                    Add
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="end">
                  <div className="space-y-1">
                    {getAvailableForFilters().map(field => (
                      <button
                        key={field}
                        onClick={() => addFilter(field)}
                        className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                      >
                        {formatFieldName(field)}
                      </button>
                    ))}
                    {getAvailableForFilters().length === 0 && (
                      <p className="text-xs text-muted-foreground px-2 py-1">All fields added</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {filters.map(field => (
              <div key={field} className="bg-white dark:bg-background rounded-lg p-3 space-y-2 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{formatFieldName(field)}</span>
                  <button onClick={() => removeFilter(field)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <Select 
                  value={filterValues[field] || "all"} 
                  onValueChange={(val) => setFilterValues({...filterValues, [field]: val})}
                >
                  <SelectTrigger className="h-8 text-xs bg-white dark:bg-background">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {(filterOptions[field] || []).slice(0, 30).map(opt => (
                      <SelectItem key={opt} value={opt}>{opt || '(Empty)'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>

        {/* Apply Button */}
        <div className="p-3 border-t bg-white dark:bg-background">
          <Button 
            onClick={handleApply} 
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            disabled={loading}
          >
            {loading ? "Loading..." : "Apply Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PivotTableWidget;
